import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'

// BragBox Terms of Service. Static legal page. Effective 2026-07-09.
// Covers accounts, content ownership, AI-output disclaimer, payments,
// termination, and liability.

const EFFECTIVE_DATE = 'July 9, 2026'

const sections = [
  { id: 'acceptance', label: '1. Accepting these terms' },
  { id: 'your-account', label: '2. Your account' },
  { id: 'your-content', label: '3. Your content' },
  { id: 'ai-output', label: '4. AI polish output' },
  { id: 'acceptable-use', label: '5. Acceptable use' },
  { id: 'plans-payment', label: '6. Plans and payment' },
  { id: 'cancellation', label: '7. Cancellation and refunds' },
  { id: 'termination', label: '8. Termination' },
  { id: 'ip', label: '9. Intellectual property' },
  { id: 'disclaimer', label: '10. Warranty disclaimer' },
  { id: 'liability', label: '11. Limitation of liability' },
  { id: 'governing-law', label: '12. Governing law and disputes' },
  { id: 'changes', label: '13. Changes to these terms' },
  { id: 'contact', label: '14. Contact' },
]

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: '#1F2A44', fontFamily: 'Manrope' }}>
        {title}
      </h2>
      <div className="text-gray-600 text-sm md:text-base leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  )
}

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Terms of Service | BragBox</title>
        <meta name="description" content="The terms that apply when you use BragBox. Written to be read, not skimmed." />
        <link rel="canonical" href="https://bragbox.ai/terms" />
      </Helmet>

      <SiteHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#1F2A44', fontFamily: 'Manrope' }}>
          Terms of Service
        </h1>
        <p className="text-gray-500 text-sm mb-8">Effective date: {EFFECTIVE_DATE}</p>

        <p className="text-gray-600 mb-8 leading-relaxed">
          These terms are the agreement between you and BragBox when you use bragbox.ai. We wrote
          them to be readable. If you have a question about anything here, email{' '}
          <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>{' '}
          before you sign up. Our <Link to="/privacy" className="underline" style={{ color: '#1F2A44' }}>Privacy Policy</Link>{' '}
          explains how we handle your data and is part of this agreement.
        </p>

        {/* Table of contents */}
        <div className="border border-gray-200 rounded-lg p-5 mb-10 bg-gray-50">
          <p className="font-semibold mb-3 text-sm" style={{ color: '#1F2A44' }}>On this page</p>
          <ul className="space-y-1.5 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-gray-600 hover:text-gray-900 underline">{s.label}</a>
              </li>
            ))}
          </ul>
        </div>

        <Section id="acceptance" title="1. Accepting these terms">
          <p>By creating an account or using BragBox, you agree to these terms. If you do not agree, do not use the service. If you are using BragBox on behalf of a company, you confirm you have authority to bind that company, and "you" means the company too.</p>
        </Section>

        <Section id="your-account" title="2. Your account">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>You must be at least 18 years old, or the age of majority where you live, whichever is higher.</li>
            <li>Give us accurate information and keep it up to date.</li>
            <li>One account per person. Keep your password to yourself. You are responsible for activity that happens under your account.</li>
            <li>Tell us at <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a> right away if you think someone else has access to your account.</li>
          </ul>
        </Section>

        <Section id="your-content" title="3. Your content">
          <p><strong>You own your wins.</strong> Everything you write in BragBox, and the documents generated from it, belongs to you. We claim no ownership of your content.</p>
          <p>So that the app can work, you grant BragBox a limited, non-exclusive license to store your content, process it, display it back to you, and send it to the subprocessors named in our Privacy Policy (for example, sending win text to Anthropic when you ask for a polish). This license exists only to run the service for you and ends when your content is deleted.</p>
          <p>You are responsible for what you write. Do not paste in material you have no right to share, such as another person's confidential information or content that breaks your employment agreements.</p>
        </Section>

        <Section id="ai-output" title="4. AI polish output">
          <p>The polish feature uses Anthropic's Claude AI model to rewrite your win text. AI output is a suggestion, not advice. It can be wrong, overstated, or miss context that matters.</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Review every polished win before you use it. You are responsible for what you submit to your manager, your review, or anyone else.</li>
            <li>Polished text is not legal, HR, or career advice.</li>
            <li>Do not present AI-polished claims you know to be inaccurate as fact.</li>
          </ul>
        </Section>

        <Section id="acceptable-use" title="5. Acceptable use">
          <p>Do not use BragBox to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Store or share content that is illegal, defamatory, or harassing.</li>
            <li>Infringe anyone's intellectual property or confidentiality rights.</li>
            <li>Probe, scan, overload, or interfere with the service, or attempt to access accounts or data that are not yours.</li>
            <li>Resell or white-label the service without our written permission.</li>
            <li>Send spam or run automated bulk signups.</li>
          </ul>
          <p>We may suspend or remove content or accounts that break these rules.</p>
        </Section>

        <Section id="plans-payment" title="6. Plans and payment">
          <p>BragBox has a free plan with limits (for example, a monthly cap on wins) and paid plans. Current prices, plan limits, and any free-trial terms are shown on the{' '}
            <Link to="/pricing" className="underline" style={{ color: '#1F2A44' }}>pricing page</Link>{' '}at the time you buy.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Subscriptions</strong> (monthly or yearly) renew automatically until you cancel. If a free trial is offered, your card is charged when the trial ends unless you cancel first.</li>
            <li><strong>Lifetime plan</strong> is a one-time payment for ongoing access to the features listed at purchase, for as long as BragBox operates. It is not a subscription and does not renew.</li>
            <li>Payments are processed by Stripe. Prices may be shown in your local currency.</li>
            <li>We may change prices for new purchases at any time. Price changes to an existing subscription take effect at your next renewal, and we will email you first.</li>
          </ul>
        </Section>

        <Section id="cancellation" title="7. Cancellation and refunds">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>You can cancel a subscription at any time from your subscription settings or by emailing <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>. You keep access until the end of the period you paid for.</li>
            <li>We do not give partial refunds for unused time, except where the law where you live requires it.</li>
            <li>If something is broken and we cannot fix it, contact us. We would rather make it right than argue over a policy.</li>
          </ul>
        </Section>

        <Section id="termination" title="8. Termination">
          <p>You can delete your account at any time by emailing <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>. Deletion is handled as described in our Privacy Policy: personal data and wins removed within 30 days.</p>
          <p>We may suspend or close your account if you materially breach these terms, and we will tell you why unless the law prevents us. If we ever discontinue BragBox entirely, we will give you at least 30 days' notice and a way to export your wins.</p>
        </Section>

        <Section id="ip" title="9. Intellectual property">
          <p>BragBox owns the platform: the software, design, branding, and everything about the service except your content. You may not copy, modify, or reverse-engineer it except where the law allows. Your wins remain yours, as section 3 says.</p>
        </Section>

        <Section id="disclaimer" title="10. Warranty disclaimer">
          <p>BragBox is provided "as is" and "as available". To the maximum extent the law allows, we disclaim all warranties, express or implied, including fitness for a particular purpose and non-infringement. We do not promise the service will be uninterrupted or error-free, or that AI output will be accurate. Nothing in this section limits rights that consumer law gives you and that cannot be waived.</p>
        </Section>

        <Section id="liability" title="11. Limitation of liability">
          <p>To the maximum extent the law allows, BragBox will not be liable for indirect, incidental, special, or consequential damages, or for lost profits, lost data, or lost opportunities (including the outcome of any performance review, promotion, or salary conversation), arising from your use of the service.</p>
          <p>Our total liability for any claim is limited to the greater of the amount you paid us in the 12 months before the claim, or 50 euro. Nothing in these terms excludes liability that cannot be excluded by law, such as liability for fraud or for death or personal injury caused by negligence.</p>
        </Section>

        <Section id="governing-law" title="12. Governing law and disputes">
          <p>These terms are governed by the laws of Ireland, and the courts of Ireland have jurisdiction over any dispute, except that if you are a consumer in the EU you keep any protection given by the mandatory laws of the country you live in.</p>
          <p>Before going to court, contact us at <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a>. Most problems can be sorted out with an email.</p>
        </Section>

        <Section id="changes" title="13. Changes to these terms">
          <p>We may update these terms as the product evolves. If a change is material, we will email account holders at least 14 days before it takes effect. Continuing to use BragBox after a change takes effect means you accept the updated terms. The current version always lives at bragbox.ai/terms with its effective date at the top.</p>
        </Section>

        <Section id="contact" title="14. Contact">
          <p>
            Email: <a href="mailto:support@bragbox.ai" className="underline" style={{ color: '#1F2A44' }}>support@bragbox.ai</a><br />
            Or use the <Link to="/contact" className="underline" style={{ color: '#1F2A44' }}>contact form</Link>.
          </p>
          <p>BragBox is operated by the BragBox operator.</p>
        </Section>
      </main>

      <SiteFooter />
    </div>
  )
}
